import supabase from '../lib/supabase';

export const analyticsService = {
  // Test connection to the analytics table
  async testConnection() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('id')
        .limit(1);
      
      if (error) {
        console.error('Analytics connection test failed:', error);
        return { success: false, error };
      }
      
      console.log('Analytics connection successful');
      return { success: true, data };
    } catch (error) {
      console.error('Analytics connection error:', error);
      return { success: false, error };
    }
  },

  // Get total bookings and revenue
  async getOverallMetrics() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('consultation_fee, status, created_at');

      if (error) {
        console.error('Error fetching overall metrics:', error);
        return { success: false, error };
      }

      const totalBookings = data.length;
      const totalRevenue = data.reduce((sum, booking) => sum + parseFloat(booking.consultation_fee || 0), 0);
      const confirmedBookings = data.filter(booking => booking.status === 'confirmed').length;
      const cancelledBookings = data.filter(booking => booking.status === 'cancelled').length;
      const pendingBookings = data.filter(booking => booking.status === 'pending').length;

      // Calculate today's bookings
      const today = new Date().toISOString().split('T')[0];
      const todayBookings = data.filter(booking => {
        const createdDate = new Date(booking.created_at).toISOString().split('T')[0];
        return createdDate === today;
      }).length;

      return {
        success: true,
        data: {
          totalBookings,
          totalRevenue,
          confirmedBookings,
          cancelledBookings,
          pendingBookings,
          todayBookings,
          confirmationRate: totalBookings > 0 ? ((confirmedBookings / totalBookings) * 100).toFixed(1) : 0
        }
      };
    } catch (error) {
      console.error('Error in getOverallMetrics:', error);
      return { success: false, error };
    }
  },

  // Get bookings by date range
  async getBookingsByDateRange(days = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('appointment_date, consultation_fee, status')
        .gte('appointment_date', startDate.toISOString().split('T')[0])
        .lte('appointment_date', endDate.toISOString().split('T')[0])
        .order('appointment_date', { ascending: true });

      if (error) {
        console.error('Error fetching bookings by date:', error);
        return { success: false, error };
      }

      // Group by date
      const bookingsByDate = {};
      const revenueByDate = {};

      data.forEach(booking => {
        const date = booking.appointment_date;
        bookingsByDate[date] = (bookingsByDate[date] || 0) + 1;
        revenueByDate[date] = (revenueByDate[date] || 0) + parseFloat(booking.consultation_fee || 0);
      });

      return {
        success: true,
        data: {
          bookingsByDate,
          revenueByDate,
          rawData: data
        }
      };
    } catch (error) {
      console.error('Error in getBookingsByDateRange:', error);
      return { success: false, error };
    }
  },

  // Get patient demographics
  async getPatientDemographics() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('patient_gender, patient_age, patient_place, wheelchair_assistance');

      if (error) {
        console.error('Error fetching demographics:', error);
        return { success: false, error };
      }

      // Gender distribution
      const genderStats = {};
      data.forEach(patient => {
        const gender = patient.patient_gender;
        genderStats[gender] = (genderStats[gender] || 0) + 1;
      });

      // Age distribution
      const ageGroups = {
        '0-18': 0,
        '19-30': 0,
        '31-45': 0,
        '46-60': 0,
        '60+': 0
      };

      data.forEach(patient => {
        const age = patient.patient_age;
        if (age <= 18) ageGroups['0-18']++;
        else if (age <= 30) ageGroups['19-30']++;
        else if (age <= 45) ageGroups['31-45']++;
        else if (age <= 60) ageGroups['46-60']++;
        else ageGroups['60+']++;
      });

      // Location distribution (top 10)
      const locationStats = {};
      data.forEach(patient => {
        const place = patient.patient_place;
        locationStats[place] = (locationStats[place] || 0) + 1;
      });

      const topLocations = Object.entries(locationStats)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10);

      // Wheelchair assistance stats
      const wheelchairStats = {
        required: data.filter(p => p.wheelchair_assistance === true).length,
        notRequired: data.filter(p => p.wheelchair_assistance === false || p.wheelchair_assistance === null).length
      };

      return {
        success: true,
        data: {
          genderStats,
          ageGroups,
          locationStats: Object.fromEntries(topLocations),
          wheelchairStats,
          totalPatients: data.length
        }
      };
    } catch (error) {
      console.error('Error in getPatientDemographics:', error);
      return { success: false, error };
    }
  },

  // Get appointment type analytics
  async getAppointmentTypeAnalytics() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('appointment_type_name, consultation_fee, duration, location, status');

      if (error) {
        console.error('Error fetching appointment analytics:', error);
        return { success: false, error };
      }

      // Appointment type distribution
      const typeStats = {};
      const revenueByType = {};
      const durationStats = {};

      data.forEach(appointment => {
        const type = appointment.appointment_type_name;
        const fee = parseFloat(appointment.consultation_fee || 0);
        const duration = appointment.duration;

        typeStats[type] = (typeStats[type] || 0) + 1;
        revenueByType[type] = (revenueByType[type] || 0) + fee;
        
        if (duration) {
          durationStats[duration] = (durationStats[duration] || 0) + 1;
        }
      });

      // Location analytics
      const locationStats = {};
      data.forEach(appointment => {
        const location = appointment.location;
        locationStats[location] = (locationStats[location] || 0) + 1;
      });

      // Status by type
      const statusByType = {};
      data.forEach(appointment => {
        const type = appointment.appointment_type_name;
        const status = appointment.status;
        
        if (!statusByType[type]) {
          statusByType[type] = {};
        }
        statusByType[type][status] = (statusByType[type][status] || 0) + 1;
      });

      return {
        success: true,
        data: {
          typeStats,
          revenueByType,
          durationStats,
          locationStats,
          statusByType
        }
      };
    } catch (error) {
      console.error('Error in getAppointmentTypeAnalytics:', error);
      return { success: false, error };
    }
  },

  // Get revenue analytics
  async getRevenueAnalytics() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('consultation_fee, appointment_date, appointment_type_name, status, created_at')
        .order('appointment_date', { ascending: true });

      if (error) {
        console.error('Error fetching revenue analytics:', error);
        return { success: false, error };
      }

      // Monthly revenue
      const monthlyRevenue = {};
      const confirmedRevenue = {};
      
      data.forEach(booking => {
        const fee = parseFloat(booking.consultation_fee || 0);
        const date = new Date(booking.appointment_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + fee;
        
        if (booking.status === 'confirmed') {
          confirmedRevenue[monthKey] = (confirmedRevenue[monthKey] || 0) + fee;
        }
      });

      // Revenue by appointment type
      const revenueByType = {};
      data.forEach(booking => {
        const type = booking.appointment_type_name;
        const fee = parseFloat(booking.consultation_fee || 0);
        revenueByType[type] = (revenueByType[type] || 0) + fee;
      });

      // Average consultation fee
      const totalRevenue = data.reduce((sum, booking) => sum + parseFloat(booking.consultation_fee || 0), 0);
      const averageFee = data.length > 0 ? totalRevenue / data.length : 0;

      return {
        success: true,
        data: {
          monthlyRevenue,
          confirmedRevenue,
          revenueByType,
          totalRevenue,
          averageFee,
          totalBookings: data.length
        }
      };
    } catch (error) {
      console.error('Error in getRevenueAnalytics:', error);
      return { success: false, error };
    }
  },

  // Get time-based analytics
  async getTimeAnalytics() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('appointment_time, appointment_date, created_at, updated_at')
        .order('appointment_date', { ascending: true });

      if (error) {
        console.error('Error fetching time analytics:', error);
        return { success: false, error };
      }

      // Peak hours analysis
      const hourStats = {};
      data.forEach(booking => {
        const time = booking.appointment_time;
        if (time) {
          // Extract hour from time string (assuming format like "10:30" or "10:30 AM")
          const hour = parseInt(time.split(':')[0]);
          if (!isNaN(hour)) {
            hourStats[hour] = (hourStats[hour] || 0) + 1;
          }
        }
      });

      // Day of week analysis
      const dayStats = {};
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      data.forEach(booking => {
        const date = new Date(booking.appointment_date);
        const dayName = dayNames[date.getDay()];
        dayStats[dayName] = (dayStats[dayName] || 0) + 1;
      });

      // Booking creation patterns
      const creationHourStats = {};
      data.forEach(booking => {
        if (booking.created_at) {
          const hour = new Date(booking.created_at).getHours();
          creationHourStats[hour] = (creationHourStats[hour] || 0) + 1;
        }
      });

      return {
        success: true,
        data: {
          hourStats,
          dayStats,
          creationHourStats
        }
      };
    } catch (error) {
      console.error('Error in getTimeAnalytics:', error);
      return { success: false, error };
    }
  },

  // Export data to CSV format
  async exportToCSV() {
    try {
      const { data, error } = await supabase
        .from('bookings_patient_data_bp2025')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching data for export:', error);
        return { success: false, error };
      }

      // Convert to CSV
      if (data.length === 0) {
        return { success: false, error: 'No data available for export' };
      }

      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape commas and quotes in CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value || '';
          }).join(',')
        )
      ].join('\n');

      return {
        success: true,
        data: csvContent,
        filename: `appointment_analytics_${new Date().toISOString().split('T')[0]}.csv`
      };
    } catch (error) {
      console.error('Error in exportToCSV:', error);
      return { success: false, error };
    }
  },

  // Subscribe to real-time changes
  subscribeToAnalytics(callback) {
    try {
      const subscription = supabase
        .channel('analytics_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'bookings_patient_data_bp2025'
        }, callback)
        .subscribe();

      return subscription;
    } catch (error) {
      console.error('Error setting up real-time subscription:', error);
      return null;
    }
  }
};